import { createClient } from '@melio/openapi-typegen-client';
import {
  type paths,
  AuthenticatorData,
  ChallengeMfaForbiddenResponse,
  clientOptions,
  CreateTokenMfaResponse,
  InvalidInputErrorResponse,
  SignUpResponse as SignUpMfaResponse,
  WebSessionResponse,
  WebSsoCompleteUnauthorizedResponse,
  WebTokenForbiddenResponse,
  WebTokenSuccessResponse,
} from '@melio/auth-service-ts-client-config';

import { config } from '../config';
import { getAgentId } from './agent-id';

export type { ChallengeMfaForbiddenResponse, WebTokenForbiddenResponse, AuthenticatorData };

// Create the auth service client
const authClient = createClient<paths>({
  ...clientOptions,
  useIamAuth: false,
  baseUrl: config.authServiceBaseUrl,
  timeout: 15_000, // in milliseconds
});

/**
 * Helper to extract error body from MelioClient error response.
 */
const extractErrorBody = <T>(error: { isHttp: boolean; body?: T; cause?: unknown }): T | undefined => {
  if ('body' in error && error.body !== undefined) {
    return error.body;
  }
  return undefined;
};

// ============================================================================
// Login
// ============================================================================

interface LoginRequest {
  email: string;
  password: string;
}

export type UserInfo = WebTokenSuccessResponse['user'];

export type LoginResult =
  | { type: 'success'; user: UserInfo }
  | { type: 'mfaRequired'; mfaToken: string; authenticators: AuthenticatorData[]; challengedAuthenticators: string[] }
  | { type: 'invalidCredentials'; message: string }
  | { type: 'accountBlocked'; message: string }
  | { type: 'tooManyAttempts'; message: string }
  | { type: 'error'; message: string };

// Secure, user-friendly error messages that don't leak sensitive information
const ERROR_MESSAGES = {
  // Login errors
  invalidCredentials: 'Invalid email or password. Please try again.',
  accountBlocked: 'Your account has been temporarily locked due to suspicious activity. Please try again later or contact support.',
  tooManyAttempts: 'Too many failed attempts. Please wait a few minutes before trying again.',
  serverError: 'Something went wrong. Please try again later.',

  // MFA errors
  wrongCode: 'Invalid verification code. Please try again.',
  sessionExpired: 'Your verification session has expired. Please start over.',
  tooManyCodeRequests: 'Too many code requests. Please wait before requesting another code.',

  // Sign up errors
  invalidPassword: 'This password is too easy to guess. Please try a more complex one.',
  invalidEmail: 'This email address is not allowed. Please use a different email.',
  signUpFailed: 'Unable to create account. Please try again.',

  // SSO errors
  ssoEmailNotVerified: "Your account email isn't verified with the provider. Please verify it and try again.",
} as const;

/**
 * Login with email and password.
 * Returns:
 * - `{ type: 'success', user }` - Login successful, session cookie set
 * - `{ type: 'mfaRequired', mfaToken, authenticators, challengedAuthenticators }` - MFA verification needed
 * - `{ type: 'invalidCredentials', message }` - Wrong email or password
 * - `{ type: 'accountBlocked', message }` - Account temporarily blocked (risk/security)
 * - `{ type: 'tooManyAttempts', message }` - Rate limited due to too many failed attempts
 * - `{ type: 'error', message }` - Server or unexpected error
 */
export const login = async ({ email, password }: LoginRequest): Promise<LoginResult> => {
  const result = await authClient.POST('/auth/web/token', {
    body: {
      grant_type: 'password',
      email,
      password,
      agent_id: getAgentId(),
    },
  });

  if (result.error) {
    const statusCode = result.statusCode;

    // 401: Invalid credentials (wrong email or password)
    if (statusCode === '401') {
      return {
        type: 'invalidCredentials',
        message: ERROR_MESSAGES.invalidCredentials,
      };
    }

    // 403: Forbidden - check specific error codes
    if (statusCode === '403') {
      const errorBody = extractErrorBody(result.error) as WebTokenForbiddenResponse | undefined;
      const errorCode = errorBody?.code;

      if (errorCode === undefined) {
        return { type: 'error', message: ERROR_MESSAGES.serverError };
      }

      switch (errorCode) {
        case 'DENIED':
          // User blocked by risk system (e.g., too many login journeys in 24hrs)
          return { type: 'accountBlocked', message: ERROR_MESSAGES.accountBlocked };
        case 'MAX_ATTEMPTS_REACHED':
          // Too many failed login attempts
          return { type: 'tooManyAttempts', message: ERROR_MESSAGES.tooManyAttempts };
        // These codes are not expected for password grant, treat as generic error
        case 'EXPIRED_MFA_SESSION':
        case 'EXPIRED_AUTHENTICATOR':
        case 'WRONG_CODE':
        case 'EXPIRED_CODE':
        case 'USERNAME_REQUIRED':
        case 'FORBIDDEN':
        case 'INVALID_GRANT':
        case 'IDENTITY_CONFLICT':
        case 'UNVERIFIED_LOCAL_ACCOUNT':
          return { type: 'error', message: ERROR_MESSAGES.serverError };
        default: {
          const _exhaustiveCheck: never = errorCode;
          return _exhaustiveCheck;
        }
      }
    }

    // 500 or other: Server error
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  // Check if MFA is required (response has mfa_token)
  if ('mfa_token' in result.data) {
    const mfaData = result.data as CreateTokenMfaResponse;
    return {
      type: 'mfaRequired',
      mfaToken: mfaData.mfa_token,
      authenticators: mfaData.authenticators,
      challengedAuthenticators: mfaData.challenged_authenticators,
    };
  }

  // Success - user is logged in
  const successData = result.data as WebTokenSuccessResponse;
  return {
    type: 'success',
    user: successData.user,
  };
};

// ============================================================================
// Verify MFA
// ============================================================================

interface MfaRequest {
  code: string;
  authenticatorId: string;
  mfaToken: string;
}

export type VerifyMfaResult =
  | { type: 'success'; user: UserInfo }
  | {
      type: 'wrongCode';
      message: string;
      remainingCodeVerificationAttempts?: number;
      remainingCodeChallengeAttempts?: number;
    }
  | { type: 'maxAttemptsReached'; message: string }
  | { type: 'sessionExpired'; message: string }
  | { type: 'error'; message: string };

/**
 * Verify MFA code.
 * Returns:
 * - `{ type: 'success', user }` - MFA verified, session cookie set
 * - `{ type: 'wrongCode', message }` - Invalid code entered
 * - `{ type: 'maxAttemptsReached', message }` - Too many failed attempts
 * - `{ type: 'sessionExpired', message }` - MFA session expired
 * - `{ type: 'error', message }` - Other error
 */
export const verifyMfa = async ({ code, authenticatorId, mfaToken }: MfaRequest): Promise<VerifyMfaResult> => {
  const result = await authClient.POST('/auth/web/token', {
    body: {
      grant_type: 'melio_mfa_token',
      mfa_token: mfaToken,
      code,
      authenticator_id: authenticatorId,
    },
  });

  if (result.error) {
    const statusCode = result.statusCode;

    // 401: Invalid/expired token
    if (statusCode === '401') {
      return { type: 'sessionExpired', message: ERROR_MESSAGES.sessionExpired };
    }

    // 403: Forbidden - check specific error codes
    if (statusCode === '403') {
      const errorBody = extractErrorBody(result.error) as WebTokenForbiddenResponse | undefined;
      const errorCode = errorBody?.code;

      if (errorCode === undefined) {
        return { type: 'error', message: ERROR_MESSAGES.serverError };
      }

      // The API may return additional fields beyond what WebTokenForbiddenResponse types
      const responseData = errorBody?.data as
        | { remainingAttempts?: number; remainingCodeVerificationAttempts?: number; remainingCodeChallengeAttempts?: number }
        | undefined;

      switch (errorCode) {
        case 'WRONG_CODE':
          return {
            type: 'wrongCode',
            message: ERROR_MESSAGES.wrongCode,
            remainingCodeVerificationAttempts: responseData?.remainingCodeVerificationAttempts,
            remainingCodeChallengeAttempts: responseData?.remainingCodeChallengeAttempts,
          };
        case 'MAX_ATTEMPTS_REACHED':
          return { type: 'maxAttemptsReached', message: ERROR_MESSAGES.tooManyAttempts };
        case 'EXPIRED_MFA_SESSION':
        case 'EXPIRED_AUTHENTICATOR':
        case 'EXPIRED_CODE':
        case 'FORBIDDEN':
        case 'USERNAME_REQUIRED':
        case 'DENIED':
        case 'INVALID_GRANT':
        case 'IDENTITY_CONFLICT':
        case 'UNVERIFIED_LOCAL_ACCOUNT':
          return { type: 'sessionExpired', message: ERROR_MESSAGES.sessionExpired };
        default: {
          const _exhaustiveCheck: never = errorCode;
          return _exhaustiveCheck;
        }
      }
    }

    // 500 or other: Server error
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  const successData = result.data as WebTokenSuccessResponse;
  return {
    type: 'success',
    user: successData.user,
  };
};

// ============================================================================
// Sign Up
// ============================================================================

interface SignUpRequest {
  email: string;
  password: string;
}

export type SignUpResult =
  | { type: 'userAlreadyValidated' }
  | { type: 'mfaRequired'; mfaToken: string; authenticators: AuthenticatorData[]; challengedAuthenticators: string[] }
  | { type: 'invalidPassword'; message: string }
  | { type: 'invalidEmail'; message: string }
  | { type: 'error'; message: string };

/**
 * Sign up with email and password.
 * Returns:
 * - `{ type: 'userAlreadyValidated' }` - User exists and is validated, proceed with login
 * - `{ type: 'mfaRequired', mfaToken, authenticators, challengedAuthenticators }` - Email verification needed
 * - `{ type: 'invalidPassword', message }` - Password doesn't meet requirements
 * - `{ type: 'error', message }` - Sign up failed
 */
export const signUp = async ({ email, password }: SignUpRequest): Promise<SignUpResult> => {
  const result = await authClient.POST('/auth/sign-up', {
    body: {
      username: email,
      password,
      client_id: config.clientId,
      agent_id: getAgentId(),
    },
  });

  if (result.error) {
    const statusCode = result.statusCode;

    // 400: Bad request
    if (statusCode === '400') {
      const errorBody = extractErrorBody(result.error) as InvalidInputErrorResponse | undefined;
      const code = errorBody?.code;

      if (!code) {
        return { type: 'error', message: ERROR_MESSAGES.signUpFailed };
      }

      switch (code) {
        case 'INVALID_PASSWORD':
          return { type: 'invalidPassword', message: ERROR_MESSAGES.invalidPassword };
        case 'INVALID_EMAIL':
          return { type: 'invalidEmail', message: ERROR_MESSAGES.invalidEmail };
        case 'INVALID_INPUT':
          return { type: 'error', message: ERROR_MESSAGES.signUpFailed };
        default: {
          // Exhaustiveness check — if a new code is added to InvalidInputErrorResponse
          // TypeScript will error here, forcing explicit handling.
          const _exhaustiveCheck: never = code;
          void _exhaustiveCheck;
          return { type: 'error', message: ERROR_MESSAGES.signUpFailed };
        }
      }
    }

    // 500 or other: Server error
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  // 204 No Content = user already validated
  if (result.statusCode === '204') {
    return { type: 'userAlreadyValidated' };
  }

  // 202 = MFA required for email verification
  const mfaData = result.data as SignUpMfaResponse;
  return {
    type: 'mfaRequired',
    mfaToken: mfaData.mfa_token,
    authenticators: mfaData.authenticators,
    challengedAuthenticators: mfaData.challenged_authenticators,
  };
};

// ============================================================================
// Challenge MFA (Resend Code)
// ============================================================================

interface ChallengeMfaRequest {
  authenticatorId: string;
  mfaToken: string;
}

export type ChallengeMfaResult =
  | { type: 'success'; remainingCodeChallengeAttempts?: number }
  | { type: 'maxAttemptsReached'; message: string }
  | { type: 'sessionExpired'; message: string }
  | { type: 'error'; message: string };

/**
 * Resend MFA verification code.
 * Returns:
 * - `{ type: 'success', remainingCodeChallengeAttempts? }` - Code sent successfully (attempts left from x-remaining-code-challenge-attempts header when present)
 * - `{ type: 'maxAttemptsReached', message }` - Too many resend attempts
 * - `{ type: 'sessionExpired', message }` - MFA session expired
 * - `{ type: 'error', message }` - Other error
 */
export const challengeMfa = async ({ authenticatorId, mfaToken }: ChallengeMfaRequest): Promise<ChallengeMfaResult> => {
  const result = await authClient.POST('/auth/mfa/challenge', {
    body: {
      authenticator_id: authenticatorId,
    },
    headers: {
      Authorization: `Bearer ${mfaToken}`,
    },
  });

  if (result.error) {
    const statusCode = result.statusCode;

    // 401: Unauthorized - invalid/expired token
    if (statusCode === '401') {
      return { type: 'sessionExpired', message: ERROR_MESSAGES.sessionExpired };
    }

    // 403: Forbidden - check specific error codes
    if (statusCode === '403') {
      const errorBody = extractErrorBody(result.error) as ChallengeMfaForbiddenResponse | undefined;
      const errorCode = errorBody?.code;

      if (errorCode === undefined) {
        return { type: 'error', message: ERROR_MESSAGES.serverError };
      }

      switch (errorCode) {
        case 'MAX_ATTEMPTS_REACHED':
          return { type: 'maxAttemptsReached', message: ERROR_MESSAGES.tooManyCodeRequests };
        case 'EXPIRED_MFA_SESSION':
        case 'EXPIRED_AUTHENTICATOR':
        case 'FORBIDDEN':
          return { type: 'sessionExpired', message: ERROR_MESSAGES.sessionExpired };
        default: {
          const _exhaustiveCheck: never = errorCode;
          return _exhaustiveCheck;
        }
      }
    }

    // 500 or other: Server error
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  // 204 success: parse x-remaining-code-challenge-attempts header if present
  const remainingHeader = result.response?.headers?.get?.('x-remaining-code-challenge-attempts');
  const parsed = remainingHeader != null && remainingHeader !== '' ? parseInt(remainingHeader, 10) : NaN;
  const remainingCodeChallengeAttempts = Number.isNaN(parsed) ? undefined : parsed;

  return { type: 'success', remainingCodeChallengeAttempts };
};

// ============================================================================
// Forgot Password
// ============================================================================

interface ForgotPasswordRequest {
  email: string;
}

export type ForgotPasswordResult = { type: 'success' } | { type: 'error'; message: string };

/**
 * Request a password reset email.
 * Returns:
 * - `{ type: 'success' }` - Email sent (or silently ignored if user doesn't exist)
 * - `{ type: 'error', message }` - Request failed
 */
export const forgotPassword = async ({ email }: ForgotPasswordRequest): Promise<ForgotPasswordResult> => {
  const result = await authClient.POST('/auth/forgot-password', {
    body: {
      username: email,
      client_id: config.clientId,
    },
  });

  if (result.error) {
    const errorBody = extractErrorBody(result.error) as { message?: string } | undefined;
    return {
      type: 'error',
      message: errorBody?.message ?? 'Failed to send reset email',
    };
  }

  return { type: 'success' };
};

// ============================================================================
// Reset Password
// ============================================================================

interface ResetPasswordRequest {
  token: string;
  password: string;
}

export type ResetPasswordResult = { type: 'success' } | { type: 'expired' } | { type: 'error'; message: string };

/**
 * Set a new password using a reset token from the reset-password link.
 * Returns:
 * - `{ type: 'success' }` - Password updated
 * - `{ type: 'expired' }` - Token missing/expired/invalid (401/403)
 * - `{ type: 'error', message }` - Request failed
 */
export const resetPassword = async ({ token, password }: ResetPasswordRequest): Promise<ResetPasswordResult> => {
  const result = await authClient.POST('/auth/reset-password', {
    body: { password },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (result.error) {
    const statusCode = result.statusCode;
    if (statusCode === '401' || statusCode === '403') {
      return { type: 'expired' };
    }
    const errorBody = extractErrorBody(result.error) as { message?: string } | undefined;
    return { type: 'error', message: errorBody?.message ?? ERROR_MESSAGES.serverError };
  }

  return { type: 'success' };
};

// ============================================================================
// Logout (Revoke Session)
// ============================================================================

export type LogoutResult =
  | { type: 'success' }
  | { type: 'error'; message: string; isNetworkError: boolean; statusCode: number; contentType: string };

/**
 * Revoke the current session by clearing the session cookie.
 * Returns:
 * - `{ type: 'success' }` - Session revoked successfully
 * - `{ type: 'error', message, isNetworkError, statusCode, contentType }` - Revocation failed.
 *   `isNetworkError` is true when the request never reached any server (offline, timeout, etc.).
 *   `statusCode`/`contentType` let the caller tell a real backend response apart from a non-JSON
 *   response from something in front of auth-web (edge/WAF block, origin outage, etc.) — see
 *   LogoutPage for how those get classified.
 */
export const logout = async (): Promise<LogoutResult> => {
  const result = await authClient.POST('/auth/web/revoke', {});

  if (result.error) {
    return {
      type: 'error',
      message: ERROR_MESSAGES.serverError,
      isNetworkError: !result.error.isHttp,
      statusCode: Number(result.statusCode) || 0,
      contentType: result.response?.headers?.get?.('content-type') ?? '',
    };
  }

  return { type: 'success' };
};

// ============================================================================
// Grant Scopes (consent screen)
// ============================================================================

export interface ScopeWithDescription {
  scope: string;
  description: string;
}

/** A pending consent interaction minted by /oauth/authorize. */
export interface GrantInteraction {
  clientId: string;
  displayName: string;
  logoUrl: string;
  missingScopes: ScopeWithDescription[];
  grantedScopes: ScopeWithDescription[];
}

export type GetGrantInteractionResult =
  | { type: 'ready'; interaction: GrantInteraction }
  | { type: 'sessionExpired' }
  | { type: 'invalid' }
  | { type: 'error'; message: string };

/**
 * Resolve a consent interaction id back to the client branding and the missing
 * and already-granted scopes (with their descriptions). Cookie-authenticated
 * (web session).
 * Returns:
 * - `{ type: 'ready', interaction }` - Interaction resolved (200)
 * - `{ type: 'sessionExpired' }` - No valid session (401); the caller resumes via the
 *   authorize redirect it received on the /grant URL
 * - `{ type: 'invalid' }` - Interaction missing, expired, or already used (404)
 * - `{ type: 'error', message }` - Server error
 */
export const getGrantInteraction = async (interactionId: string): Promise<GetGrantInteractionResult> => {
  const result = await authClient.GET('/auth/web/interactions/{interactionId}', {
    params: { path: { interactionId } },
  });

  if (result.error) {
    if (result.statusCode === '401') {
      return { type: 'sessionExpired' };
    }
    if (result.statusCode === '404') {
      return { type: 'invalid' };
    }
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  const { client_id, display_name, logo_url, missing_scopes, granted_scopes } = result.data;
  return {
    type: 'ready',
    interaction: {
      clientId: client_id,
      displayName: display_name,
      logoUrl: logo_url,
      missingScopes: missing_scopes,
      grantedScopes: granted_scopes,
    },
  };
};

interface GrantScopesRequest {
  /** The consent interaction id from the /grant URL. */
  interactionId: string;
}

export type GrantScopesResult =
  | { type: 'success' }
  | { type: 'sessionExpired' }
  | { type: 'invalidInteraction' }
  | { type: 'error'; message: string };

/**
 * Approve a consent interaction: the backend persists the missing scopes
 * recorded on it and consumes it (single-use). The scopes and client come
 * from the server-stored interaction, not from this request.
 * Cookie-authenticated (web session); the backend returns 204 on success.
 * Returns:
 * - `{ type: 'success' }` - Grant persisted (204)
 * - `{ type: 'sessionExpired' }` - No valid session (401); the caller resumes via the
 *   authorize redirect it received on the /grant URL
 * - `{ type: 'invalidInteraction' }` - Interaction missing, expired, or already used (404)
 * - `{ type: 'error', message }` - Invalid request or server error
 */
export const grantScopes = async ({ interactionId }: GrantScopesRequest): Promise<GrantScopesResult> => {
  const result = await authClient.POST('/auth/web/grant', {
    body: {
      interaction_id: interactionId,
    },
  });

  if (result.error) {
    if (result.statusCode === '401') {
      return { type: 'sessionExpired' };
    }
    if (result.statusCode === '404') {
      return { type: 'invalidInteraction' };
    }
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  return { type: 'success' };
};

// ============================================================================
// Get Session
// ============================================================================

export type GetSessionResult = { type: 'authenticated'; user: WebSessionResponse } | { type: 'unauthenticated' };

/**
 * Get current session info.
 * Returns:
 * - `{ type: 'authenticated', user }` - Valid session exists
 * - `{ type: 'unauthenticated' }` - No valid session
 */
export const getSession = async (): Promise<GetSessionResult> => {
  const result = await authClient.GET('/auth/web/session');

  if (result.error) {
    return { type: 'unauthenticated' };
  }

  return {
    type: 'authenticated',
    user: result.data,
  };
};

// ============================================================================
// SSO Complete
// ============================================================================

interface CompleteSsoRequest {
  ssoState: string;
  codeVerifier: string;
}

export type SsoCompleteResult =
  | { type: 'success'; user: UserInfo }
  | { type: 'emailNotVerified'; message: string }
  | { type: 'error'; message: string };

export const completeSso = async ({ ssoState, codeVerifier }: CompleteSsoRequest): Promise<SsoCompleteResult> => {
  const result = await authClient.POST('/auth/web/sso/complete', {
    body: {
      sso_state: ssoState,
      code_verifier: codeVerifier,
    },
  });

  if (result.error) {
    if (result.statusCode === '401') {
      const errorBody = extractErrorBody<WebSsoCompleteUnauthorizedResponse>(result.error);
      if (errorBody?.code === 'email_not_verified') {
        return { type: 'emailNotVerified', message: ERROR_MESSAGES.ssoEmailNotVerified };
      }
    }
    return { type: 'error', message: ERROR_MESSAGES.serverError };
  }

  return {
    type: 'success',
    user: result.data.user,
  };
};
